/**
 * Sprint 5 client feedback 2026-07-11 (Iter6 #1) — canonical Source
 * chip renderer used by every DD / SPA / Risk report table so all
 * three reports display source references in one consistent
 * compact format:
 *
 *     InvMemo p.32
 *     FinancialDD p.8
 *     TechnicalDD p.70
 *     ICRes p.4
 *
 * The chip is a plain <span className="source-tag"> pill, styled by
 * index.css. Full document names ("Investment Memo", "Financial DD
 * Report") and their backend subtype codes ("InvestmentMemo",
 * "FinancialDD") both map to the same short label so the chip reads
 * the same regardless of which shape the BE emitted.
 */

import type { ReactNode } from 'react';

/* Full-name → short-label. Used when the BE surface names read as
   the friendly document label (typical DD attributes). */
const _NAME_TO_SHORT: Record<string, string> = {
  'Investment Memo': 'InvMemo',
  'Financial DD Report': 'FinancialDD',
  'Financial DD': 'FinancialDD',
  'Technical DD Report': 'TechnicalDD',
  'Technical DD': 'TechnicalDD',
  'IC Resolution': 'ICRes',
  'IC Memo': 'ICRes',
  'Share Purchase Agreement': 'SPA',
  'Shareholders Agreement': 'SHA',
  'Escrow Agreement': 'Escrow',
  'Optional Document': 'Other',
  'Supporting Document': 'Other',
};

/* Backend subtype code → short label. Used when a raw subtype
   ("InvestmentMemo") leaks through the payload instead of the
   friendly label. */
const _SUBTYPE_TO_SHORT: Record<string, string> = {
  InvestmentMemo: 'InvMemo',
  FinancialDD: 'FinancialDD',
  TechnicalDD: 'TechnicalDD',
  ICResolution: 'ICRes',
  ICRes: 'ICRes',
  SPA: 'SPA',
  SHA: 'SHA',
  Escrow: 'Escrow',
  Other: 'Other',
};

/* Sprint 5 client feedback 2026-07-12 — placeholder values that mean "no
   source". The BE emits an em-dash (and occasionally "N/A" / "None") in the
   source field rather than null. shortSourceLabel() falls back to returning
   its input verbatim for unrecognised documents, so those placeholders were
   being treated as document NAMES and rendered inside a bordered chip — a
   boxed "—" sitting in the Source column as if a dash were a document.
   Normalise them to empty here, and every report table (DD / SPA / Risk)
   renders the plain muted dash instead. */
const _NO_SOURCE = new Set([
  '—', '–', '-', '--', 'n/a', 'na', 'none', 'null', 'undefined',
  'not found', 'not available', 'not found in documents',
]);

function _isNoSource(value: string): boolean {
  return _NO_SOURCE.has(value.trim().toLowerCase());
}

/** Return the short chip label for a source document string. Falls
 *  back to the input verbatim when nothing in the map matches — so
 *  the report never renders an empty pill for an unrecognised source.
 *  Placeholder values ("—", "N/A", …) resolve to '' — they are not
 *  documents. */
export function shortSourceLabel(doc: string | null | undefined): string {
  if (doc == null) return '';
  const trimmed = String(doc).trim();
  if (!trimmed || _isNoSource(trimmed)) return '';
  if (_NAME_TO_SHORT[trimmed]) return _NAME_TO_SHORT[trimmed];
  if (_SUBTYPE_TO_SHORT[trimmed]) return _SUBTYPE_TO_SHORT[trimmed];
  /* Case-insensitive fallback so "financial dd report" and
     "INVESTMENT MEMO" still match. */
  const upperKey = Object.keys(_NAME_TO_SHORT).find(
    (k) => k.toLowerCase() === trimmed.toLowerCase(),
  );
  if (upperKey) return _NAME_TO_SHORT[upperKey];
  return trimmed;
}

/** Render the standardised Source chip. When both `doc` and `page`
 *  are empty, returns a muted em-dash so the cell is never blank.
 *  Otherwise wraps the short-label + optional page in a
 *  `<span className="source-tag">`. */
export function SourceChip({
  doc,
  page,
}: {
  doc: string | null | undefined;
  page: number | string | null | undefined;
}): ReactNode {
  const label = shortSourceLabel(doc);
  // A placeholder page ("—", "N/A") is not a page either — without this the
  // chip would still render, just with a dash inside it.
  const hasPage =
    page != null
    && String(page).trim() !== ''
    && !_isNoSource(String(page));
  if (!label && !hasPage) {
    // No source at all — a plain muted dash, NOT a bordered chip. A chip
    // implies a citation exists.
    return <span className="t-muted">—</span>;
  }
  return (
    <span className="source-tag">
      {label || '—'}
      {hasPage ? ` p.${String(page)}` : ''}
    </span>
  );
}
